import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { createAppError } from 'utils/helpers';

export const useFetchData = <R, A>(service: ((...args: A[]) => Promise<R>) | null, ...args: A[]) => {
	const { addAppError } = useContext<IAppContext>(AppContext);
	const [response, setResponse] = useState<R | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [responseError, setResponseError] = useState<boolean>(false);
	const [needRefetch, setNeedRefetch] = useState<boolean>(false);
	const mounted = useRef<boolean>(true);

	const refetch = useCallback(() => {
		// Toggle state to re-run useEffect
		setNeedRefetch((prevState) => !prevState);
	}, [service]);

	const getData = async () => {
		if (service) {
			try {
				setResponseError(false);
				setLoading(true);
				const response = await service(...args);
				if (mounted.current) {
					setResponse(response);
				}
			} catch (err) {
				if (mounted.current) {
					setResponseError(true);
					const error = err as Error;
					const appError = createAppError(error, refetch);
					addAppError(appError);
				}
			} finally {
				if (mounted.current) {
					setLoading(false);
				}
			}
		}
	};

	useEffect(() => {
		getData();
	}, [needRefetch]);

	useEffect(() => {
		return () => {
			mounted.current = false;
		};
	}, []);

	return {
		response,
		loading,
		refetch,
		responseError,
	};
};
